import { useMemo } from 'react'
import { useAudioBibleStore } from '../stores/audioBibleStore'
import { getBookById, getNextChapter, getPrevChapter } from '../lib/bibleBooks'
import { VoicePickerSheet } from './VoicePickerSheet'

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

const SPEED_CYCLE = [1, 1.25, 1.5, 0.75]

export function BibleFullScreenPlayer() {
  const view = useAudioBibleStore((s) => s.view)
  const bookId = useAudioBibleStore((s) => s.bookId)
  const chapter = useAudioBibleStore((s) => s.chapter)
  const translation = useAudioBibleStore((s) => s.translation)
  const status = useAudioBibleStore((s) => s.status)
  const currentPositionSec = useAudioBibleStore((s) => s.currentPositionSec)
  const totalDurationSec = useAudioBibleStore((s) => s.totalDurationSec)
  const currentVerse = useAudioBibleStore((s) => s.currentVerse)
  const clips = useAudioBibleStore((s) => s.clips)
  const speed = useAudioBibleStore((s) => s.speed)
  const autoNext = useAudioBibleStore((s) => s.autoNext)
  const voiceId = useAudioBibleStore((s) => s.voiceId)
  const voices = useAudioBibleStore((s) => s.voices)
  const voicePickerOpen = useAudioBibleStore((s) => s.voicePickerOpen)
  const errorMessage = useAudioBibleStore((s) => s.errorMessage)

  const collapse = useAudioBibleStore((s) => s.collapse)
  const play = useAudioBibleStore((s) => s.play)
  const pause = useAudioBibleStore((s) => s.pause)
  const resume = useAudioBibleStore((s) => s.resume)
  const seek = useAudioBibleStore((s) => s.seek)
  const setSpeed = useAudioBibleStore((s) => s.setSpeed)
  const openVoicePicker = useAudioBibleStore((s) => s.openVoicePicker)
  const toggleAutoNext = useAudioBibleStore((s) => s.toggleAutoNext)

  const { prevContextText, activeText, nextContextText } = useMemo(() => {
    if (!clips.length || currentVerse == null) {
      return { prevContextText: '', activeText: '', nextContextText: '' }
    }
    const idx = clips.findIndex((c) => c.verse === currentVerse)
    const active = idx >= 0 ? `verse ${clips[idx].verse}` : ''
    const prev = idx > 0 ? `verse ${clips[idx - 1].verse}` : ''
    const next = idx + 1 < clips.length ? `verse ${clips[idx + 1].verse}` : ''
    return { prevContextText: prev, activeText: active, nextContextText: next }
  }, [clips, currentVerse])

  if (view !== 'fullscreen') return null
  if (!bookId || !chapter) return null

  const bookName = getBookById(bookId)?.name ?? bookId
  const hasNextChapter = !!getNextChapter(bookId, chapter)
  const hasPrevChapter = !!getPrevChapter(bookId, chapter)
  const progressPct = totalDurationSec > 0 ? (currentPositionSec / totalDurationSec) * 100 : 0
  const voiceName = voices.find((v) => v.id === voiceId)?.name ?? voiceId.split(':').slice(-1)[0]

  const cycleSpeed = () => {
    const idx = SPEED_CYCLE.indexOf(speed)
    const next = SPEED_CYCLE[(idx + 1) % SPEED_CYCLE.length]
    setSpeed(next)
  }

  const onScrubClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!totalDurationSec) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = (e.clientX - rect.left) / rect.width
    seek(Math.max(0, Math.min(ratio, 1)) * totalDurationSec)
  }

  const goNextChapter = () => {
    const next = getNextChapter(bookId, chapter)
    if (next) void play(next.bookId, next.chapter, translation, { voiceId })
  }
  const goPrevChapter = () => {
    const prev = getPrevChapter(bookId, chapter)
    if (prev) void play(prev.bookId, prev.chapter, translation, { voiceId })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #1a1a1d 0%, #0a0a0b 100%)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
      }}
    >
      {/* Collapse */}
      <button
        type="button"
        onClick={collapse}
        aria-label="Collapse player"
        className="flex justify-center py-2 text-text-muted"
      >
        <span className="text-lg">▼</span>
      </button>

      {/* Header */}
      <div className="text-center px-6 py-2">
        <div className="text-text-primary font-bold text-lg">{bookName} {chapter}</div>
        <div className="text-text-muted text-xs mt-1">{translation}</div>
      </div>

      {/* Active verse area */}
      <div className="flex-1 flex flex-col items-center justify-center text-center px-8 min-h-0">
        {status === 'synthesizing' && (
          <div className="text-text-muted text-sm">Preparing audio…</div>
        )}
        {status === 'error' && (
          <div className="text-error text-sm px-4">{errorMessage ?? 'Audio unavailable'}</div>
        )}
        {status !== 'synthesizing' && status !== 'error' && (
          <>
            <div className="text-text-muted text-xs leading-snug">{prevContextText}</div>
            <div className="text-accent text-2xl leading-relaxed my-6 font-medium">
              {activeText || '…'}
            </div>
            <div className="text-text-muted text-xs leading-snug">{nextContextText}</div>
          </>
        )}
      </div>

      {/* Scrub bar */}
      <div className="px-6 mb-4">
        <div
          onClick={onScrubClick}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={totalDurationSec}
          aria-valuenow={currentPositionSec}
          className="h-1 bg-surface rounded cursor-pointer relative"
        >
          <div
            className="h-full bg-accent rounded transition-[width] duration-200"
            style={{ width: `${Math.min(progressPct, 100)}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-accent rounded-full"
            style={{ left: `${Math.min(progressPct, 100)}%`, transform: 'translate(-50%, -50%)' }}
          />
        </div>
        <div className="flex justify-between text-xs text-text-muted mt-2">
          <span>{formatTime(currentPositionSec)}</span>
          <span>{formatTime(totalDurationSec)}</span>
        </div>
      </div>

      {/* Main controls */}
      <div className="flex items-center justify-center gap-6 mb-4">
        <button
          type="button"
          onClick={goPrevChapter}
          disabled={!hasPrevChapter}
          aria-label="Previous chapter"
          className="w-10 h-10 rounded-full border border-surface flex items-center justify-center text-text-primary disabled:opacity-30"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => (status === 'playing' ? pause() : resume())}
          aria-label={status === 'playing' ? 'Pause' : 'Play'}
          className="w-16 h-16 rounded-full bg-accent text-surface flex items-center justify-center"
        >
          {status === 'playing' ? (
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        <button
          type="button"
          onClick={goNextChapter}
          disabled={!hasNextChapter}
          aria-label="Next chapter"
          className="w-10 h-10 rounded-full border border-surface flex items-center justify-center text-text-primary disabled:opacity-30"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 18l8.5-6L6 6v12zm10-12v12h2V6z" />
          </svg>
        </button>
      </div>

      {/* Pills row */}
      <div className="flex items-center justify-center gap-2 px-4 flex-wrap">
        <button
          type="button"
          onClick={cycleSpeed}
          className="px-3 py-1.5 rounded-full border border-surface text-xs text-text-primary"
        >
          {speed}× speed
        </button>
        <button
          type="button"
          onClick={openVoicePicker}
          className="px-3 py-1.5 rounded-full border border-surface text-xs text-text-primary"
        >
          Voice: {voiceName} ▾
        </button>
        <button
          type="button"
          onClick={toggleAutoNext}
          className={`px-3 py-1.5 rounded-full border border-surface text-xs ${autoNext ? 'text-accent' : 'text-text-muted'}`}
        >
          Auto-next {autoNext ? '✓' : '○'}
        </button>
      </div>

      {voicePickerOpen && <VoicePickerSheet />}
    </div>
  )
}
